import React, { useState, useEffect, useCallback } from 'react';
import { PageHeader } from '../design';
import { useSuperAdminAuth } from '../../../contexts/SuperAdminAuthContext';
import { governanceAPI } from '../../../lib/governanceApi';
import { toast } from 'sonner';
import { 
  Activity, 
  RefreshCw, 
  Bot, 
  ShieldCheck, 
  AlertTriangle, 
  CheckCircle, 
  Settings, 
  Save,
  TrendingUp,
  TrendingDown,
  Minus,
  Download,
  Search,
  Filter,
  History,
  Info,
  Pause,
  Play,
  Eye,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Zap
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Progress } from '../../../components/ui/progress';
import { Skeleton } from '../../../components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '../../../components/ui/alert';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Slider } from '../../../components/ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';

/**
 * Enhanced Confidence Page - Governance Panel
 * 
 * Features:
 * - Platform-wide confidence overview
 * - All agents with scores, trends, status
 * - Confidence trend chart
 * - Per-agent threshold configuration
 * - Alert history and audit trail
 * - Auto-refresh
 * - Export functionality
 */
export default function Confidence() {
  const { isAuthenticated } = useSuperAdminAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [timeRange, setTimeRange] = useState('30d');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  
  // Edit states
  const [globalThreshold, setGlobalThreshold] = useState(0.6);
  const [savingGlobal, setSavingGlobal] = useState(false);
  const [agentThresholdDialog, setAgentThresholdDialog] = useState({ open: false, agent: null });
  const [agentThreshold, setAgentThreshold] = useState(0.6);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: null, data: null });
  
  // Auto-refresh
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const response = await governanceAPI.getEnhancedConfidenceDashboard(timeRange);
      setDashboardData(response.data);
      setGlobalThreshold(response.data?.overview?.global_threshold || 0.6);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('[Confidence] Failed to fetch:', err);
      setError('Failed to load confidence data. Please try again.');
      toast.error('Failed to load confidence data');
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    if (isAuthenticated) {
      setLoading(true);
      fetchData();
    }
  }, [isAuthenticated, fetchData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh || !isAuthenticated) return;
    
    const interval = setInterval(() => {
      fetchData();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [autoRefresh, isAuthenticated, fetchData]);

  // Filter agents
  const filteredAgents = React.useMemo(() => {
    if (!dashboardData?.agents) return [];
    
    return dashboardData.agents.filter(agent => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!agent.agent_name?.toLowerCase().includes(query)) {
          return false;
        }
      }
      if (statusFilter !== 'all' && agent.status !== statusFilter) {
        return false;
      }
      return true;
    });
  }, [dashboardData?.agents, searchQuery, statusFilter]);

  // Paginate agents
  const paginatedAgents = React.useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredAgents.slice(start, start + pageSize);
  }, [filteredAgents, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredAgents.length / pageSize);

  const handleSaveGlobalThreshold = async () => {
    setConfirmDialog({
      open: true,
      type: 'global',
      data: { threshold: globalThreshold }
    });
  };

  const confirmSaveGlobalThreshold = async () => {
    setSavingGlobal(true);
    setConfirmDialog({ open: false, type: null, data: null });
    try {
      await governanceAPI.setGlobalConfidenceThreshold({
        threshold: globalThreshold,
        reason: 'SuperAdmin updated global threshold'
      });
      toast.success(`Global threshold updated to ${(globalThreshold * 100).toFixed(0)}%`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update global threshold');
    } finally {
      setSavingGlobal(false);
    }
  };

  const openAgentThresholdDialog = (agent) => {
    setAgentThresholdDialog({ open: true, agent });
    setAgentThreshold(agent.threshold || dashboardData?.overview?.global_threshold || 0.6);
  };

  const handleSaveAgentThreshold = async () => {
    try {
      const agentName = agentThresholdDialog.agent?.agent_name;
      await governanceAPI.setAgentConfidenceThreshold(agentName, {
        threshold: agentThreshold,
        reason: 'SuperAdmin updated agent threshold'
      });
      toast.success(`Threshold updated for ${agentName}`);
      setAgentThresholdDialog({ open: false, agent: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update threshold');
    }
  };

  const handleResumeAgent = async (agent) => {
    try {
      await governanceAPI.resumePausedAgent(agent.agent_name, {
        reason: 'SuperAdmin manually resumed agent'
      });
      toast.success(`${agent.agent_name} resumed successfully`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resume agent');
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    if (!dashboardData?.agents) return;
    
    const headers = ['Agent Name', 'Score (%)', 'Threshold (%)', 'Status', 'Trend', 'Executions', 'Last Updated'];
    const rows = dashboardData.agents.map(a => [
      a.agent_name,
      a.score_percent,
      a.threshold_percent,
      a.status,
      a.trend,
      a.executions_count,
      a.last_updated || ''
    ]);
    
    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `confidence-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Confidence report exported');
  };

  // Get status color/badge
  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy':
        return 'bg-green-500 text-white';
      case 'warning':
        return 'bg-amber-500 text-white';
      case 'critical':
        return 'bg-orange-500 text-white';
      case 'paused':
        return 'bg-red-500 text-white';
      default:
        return 'bg-slate-500 text-white';
    }
  };

  const getStatusLabel = (status) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up':
        return <TrendingUp className="h-4 w-4 text-green-500" />;
      case 'down':
        return <TrendingDown className="h-4 w-4 text-red-500" />;
      default:
        return <Minus className="h-4 w-4 text-slate-400" />;
    }
  };

  const getProgressColor = (score) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-amber-500';
    if (score >= 50) return 'bg-orange-500';
    return 'bg-red-500';
  };

  // Loading skeleton
  if (loading) {
    return (
      <div className="space-y-6" data-testid="confidence-loading">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid gap-4 md:grid-cols-4">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
        <Skeleton className="h-48" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6" data-testid="confidence-error">
        <Alert className="border-red-300 dark:border-red-500/50 bg-red-50 dark:bg-red-950/20">
          <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
          <AlertTitle className="text-red-700 dark:text-red-300">Error Loading Data</AlertTitle>
          <AlertDescription className="text-red-600 dark:text-red-400">
            {error}
          </AlertDescription>
        </Alert>
        <Button onClick={() => { setLoading(true); fetchData(); }}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  const overview = dashboardData?.overview || {};
  const severitySummary = dashboardData?.severity_summary || {};
  const confidenceTrend = dashboardData?.confidence_trend || [];
  const alertHistory = dashboardData?.alert_history || [];
  const auditTrail = dashboardData?.audit_trail || [];

  return (
    <TooltipProvider>
      <div className="space-y-6" data-testid="confidence">
        {/* Header */}
        <PageHeader
          title="Confidence Monitoring"
          subtitle={`Track and configure agent confidence thresholds • Last updated: ${lastRefresh.toLocaleTimeString()}`}
          actions={
            <div className="flex items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="sm" onClick={exportToCSV} className="border-slate-200 dark:border-slate-700">
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </TooltipTrigger>
              <TooltipContent>Download confidence report as CSV</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setAutoRefresh(!autoRefresh)}
                  className={autoRefresh ? 'border-green-500 text-green-600' : 'border-slate-200 dark:border-slate-700'}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
                  {autoRefresh ? 'Auto' : 'Manual'}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{autoRefresh ? 'Auto-refresh every 30s' : 'Click to enable auto-refresh'}</TooltipContent>
            </Tooltip>
            <Button variant="outline" size="icon" onClick={() => { setLoading(true); fetchData(); }}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
          }
        />

        {/* Overview Cards */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Platform Score</p>
                  <div className="text-3xl font-bold text-slate-900 dark:text-white">
                    {overview.platform_score_percent?.toFixed(0) || 80}%
                  </div>
                  <div className="flex items-center gap-1 mt-1">
                    {overview.overall_trend === 'improving' && <TrendingUp className="h-3 w-3 text-green-500" />}
                    {overview.overall_trend === 'declining' && <TrendingDown className="h-3 w-3 text-red-500" />}
                    {overview.overall_trend === 'stable' && <Minus className="h-3 w-3 text-slate-400" />}
                    <span className={`text-xs ${
                      overview.overall_trend === 'improving' ? 'text-green-500' : 
                      overview.overall_trend === 'declining' ? 'text-red-500' : 'text-slate-400'
                    }`}>
                      {overview.overall_trend || 'Stable'}
                    </span>
                  </div>
                </div>
                <div className={`h-14 w-14 rounded-full flex items-center justify-center ${
                  overview.platform_score_percent >= 80 ? 'bg-green-100 dark:bg-green-500/20' :
                  overview.platform_score_percent >= 60 ? 'bg-amber-100 dark:bg-amber-500/20' :
                  'bg-red-100 dark:bg-red-500/20'
                }`}>
                  <Activity className={`h-7 w-7 ${
                    overview.platform_score_percent >= 80 ? 'text-green-600 dark:text-green-400' :
                    overview.platform_score_percent >= 60 ? 'text-amber-600 dark:text-amber-400' :
                    'text-red-600 dark:text-red-400'
                  }`} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Total Agents</p>
                  <div className="text-3xl font-bold text-slate-900 dark:text-white">
                    {overview.total_agents || 0}
                  </div>
                  <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                    Being monitored
                  </p>
                </div>
                <div className="h-12 w-12 rounded-lg bg-blue-100 dark:bg-blue-500/20 flex items-center justify-center">
                  <Bot className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Global Threshold</p>
                  <div className="text-3xl font-bold text-slate-900 dark:text-white">
                    {overview.global_threshold_percent || 60}%
                  </div>
                  <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                    {overview.threshold_frozen ? 'Frozen' : 'Editable'}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-lg bg-purple-100 dark:bg-purple-500/20 flex items-center justify-center">
                  <Settings className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Paused Agents</p>
                  <div className={`text-3xl font-bold ${
                    severitySummary.paused > 0 ? 'text-red-600 dark:text-red-400' : 'text-slate-900 dark:text-white'
                  }`}>
                    {severitySummary.paused || 0}
                  </div>
                  <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                    Below threshold
                  </p>
                </div>
                <div className={`h-12 w-12 rounded-lg ${
                  severitySummary.paused > 0 ? 'bg-red-100 dark:bg-red-500/20' : 'bg-green-100 dark:bg-green-500/20'
                } flex items-center justify-center`}>
                  {severitySummary.paused > 0 ? (
                    <Pause className="h-6 w-6 text-red-600 dark:text-red-400" />
                  ) : (
                    <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Severity Summary & Trend Chart */}
        <div className="grid gap-4 lg:grid-cols-3">
          {/* Severity Summary */}
          <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-slate-900 dark:text-white flex items-center gap-2">
                <ShieldCheck className="h-4 w-4" />
                Agents by Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-500/30">
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">{severitySummary.healthy || 0}</div>
                  <p className="text-xs text-green-500 dark:text-green-400">Healthy (≥80%)</p>
                </div>
                <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30">
                  <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">{severitySummary.warning || 0}</div>
                  <p className="text-xs text-amber-500 dark:text-amber-400">Warning (60-79%)</p>
                </div>
                <div className="p-3 rounded-lg bg-orange-50 dark:bg-orange-500/10 border border-orange-200 dark:border-orange-500/30">
                  <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">{severitySummary.critical || 0}</div>
                  <p className="text-xs text-orange-500 dark:text-orange-400">Critical (50-59%)</p>
                </div>
                <div className="p-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30">
                  <div className="text-2xl font-bold text-red-600 dark:text-red-400">{severitySummary.paused || 0}</div>
                  <p className="text-xs text-red-500 dark:text-red-400">Paused (&lt;50%)</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Confidence Trend Chart */}
          <Card className="lg:col-span-2 bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base text-slate-900 dark:text-white flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Confidence Trend
                </CardTitle>
                <Select value={timeRange} onValueChange={setTimeRange}>
                  <SelectTrigger className="w-24 h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7d">7 days</SelectItem>
                    <SelectItem value="14d">14 days</SelectItem>
                    <SelectItem value="30d">30 days</SelectItem>
                    <SelectItem value="90d">90 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-32 flex items-end gap-1">
                {confidenceTrend.length === 0 ? (
                  <div className="w-full h-full flex items-center justify-center text-slate-400">
                    No trend data available
                  </div>
                ) : (
                  confidenceTrend.map((day, idx) => {
                    const heightPercent = day.score_percent || 80;
                    return (
                      <Tooltip key={idx}>
                        <TooltipTrigger asChild>
                          <div className="flex-1 flex flex-col items-center gap-1">
                            <div 
                              className={`w-full rounded-t transition-all hover:opacity-80 ${getProgressColor(day.score_percent)}`}
                              style={{ height: `${Math.max(heightPercent, 5)}%` }}
                            />
                            <span className="text-[10px] text-slate-400 truncate w-full text-center">
                              {day.label?.split(' ')[1] || ''}
                            </span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="font-medium">{day.label}</p>
                          <p className="text-sm">{day.score_percent?.toFixed(1)}%</p>
                        </TooltipContent>
                      </Tooltip>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Global Configuration */}
        <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-slate-900 dark:text-white">
                  <Settings className="h-5 w-5" />
                  Global Confidence Configuration
                </CardTitle>
                <CardDescription className="text-slate-500 dark:text-slate-400">
                  Set the default confidence threshold for all agents
                </CardDescription>
              </div>
              <Badge variant={overview.threshold_frozen ? 'destructive' : 'default'}>
                {overview.threshold_frozen ? 'Frozen' : 'Editable'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Global Threshold Slider */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Label className="text-slate-700 dark:text-slate-300">Default Confidence Threshold</Label>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-4 w-4 text-slate-400" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p><strong>Lower = more lenient:</strong> Agents continue running even with lower confidence</p>
                        <p className="mt-1"><strong>Higher = stricter:</strong> Agents are paused sooner for review</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <span className="text-2xl font-bold text-slate-900 dark:text-white">{(globalThreshold * 100).toFixed(0)}%</span>
                </div>
                <Slider
                  value={[globalThreshold * 100]}
                  onValueChange={(v) => setGlobalThreshold(v[0] / 100)}
                  max={100}
                  min={30}
                  step={5}
                  className="w-full"
                  disabled={overview.threshold_frozen}
                />
                <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400">
                  <span>30% (Lenient)</span>
                  <span>60% (Default)</span>
                  <span>100% (Strict)</span>
                </div>
              </div>

              {/* Configuration Details */}
              <div className="grid grid-cols-3 gap-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="text-center p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg cursor-help">
                      <div className="text-lg font-bold text-slate-900 dark:text-white">{overview.streak_limit || 3}</div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Streak Limit</p>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    Number of consecutive low-confidence executions before auto-pause
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="text-center p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg cursor-help">
                      <div className="text-lg font-bold text-slate-900 dark:text-white">{overview.rolling_window || 10}</div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Rolling Window</p>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    Number of recent executions used to calculate rolling average
                  </TooltipContent>
                </Tooltip>
                <div className="text-center p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
                  <div className="text-lg font-bold text-slate-900 dark:text-white">{severitySummary.paused || 0}</div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Paused Agents</p>
                </div>
              </div>

              {/* Save Button */}
              <div className="flex justify-end">
                <Button 
                  onClick={handleSaveGlobalThreshold} 
                  disabled={savingGlobal || overview.threshold_frozen}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {savingGlobal ? 'Saving...' : 'Save Global Threshold'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs: Agents / Alerts / Audit */}
        <Tabs defaultValue="agents" className="w-full">
          <TabsList className="grid w-full grid-cols-3 max-w-md">
            <TabsTrigger value="agents" className="flex items-center gap-2">
              <Bot className="h-4 w-4" />
              All Agents
            </TabsTrigger>
            <TabsTrigger value="alerts" className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Alerts
              {alertHistory.length > 0 && (
                <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1">
                  {alertHistory.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="audit" className="flex items-center gap-2">
              <History className="h-4 w-4" />
              Audit Trail
            </TabsTrigger>
          </TabsList>

          {/* Agents Tab */}
          <TabsContent value="agents" className="mt-4">
            <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <CardTitle className="text-base text-slate-900 dark:text-white">
                    Agent Confidence Scores ({filteredAgents.length})
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        type="text"
                        placeholder="Search agents..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9 h-9 w-48"
                      />
                    </div>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-32 h-9">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="healthy">Healthy</SelectItem>
                        <SelectItem value="warning">Warning</SelectItem>
                        <SelectItem value="critical">Critical</SelectItem>
                        <SelectItem value="paused">Paused</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {filteredAgents.length === 0 ? (
                  <div className="py-12 text-center">
                    <Activity className="h-12 w-12 mx-auto text-green-500 mb-4" />
                    <p className="text-lg font-medium text-slate-900 dark:text-white">
                      {searchQuery || statusFilter !== 'all' ? 'No Matching Agents' : 'No Agents Found'}
                    </p>
                    <p className="text-slate-500 dark:text-slate-400">
                      {searchQuery || statusFilter !== 'all' ? 'Try adjusting your filters' : 'No AI agents have been executed yet'}
                    </p>
                  </div>
                ) : (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Agent</TableHead>
                          <TableHead>Score</TableHead>
                          <TableHead>Trend</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Threshold</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paginatedAgents.map((agent) => (
                          <TableRow key={agent.agent_name} className={agent.status === 'paused' ? 'bg-red-50/50 dark:bg-red-950/10' : ''}>
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                                  agent.status === 'healthy' ? 'bg-green-100 dark:bg-green-500/20' :
                                  agent.status === 'warning' ? 'bg-amber-100 dark:bg-amber-500/20' :
                                  agent.status === 'critical' ? 'bg-orange-100 dark:bg-orange-500/20' :
                                  'bg-red-100 dark:bg-red-500/20'
                                }`}>
                                  <Bot className={`h-5 w-5 ${
                                    agent.status === 'healthy' ? 'text-green-600 dark:text-green-400' :
                                    agent.status === 'warning' ? 'text-amber-600 dark:text-amber-400' :
                                    agent.status === 'critical' ? 'text-orange-600 dark:text-orange-400' :
                                    'text-red-600 dark:text-red-400'
                                  }`} />
                                </div>
                                <div>
                                  <p className="font-medium text-slate-900 dark:text-white">{agent.agent_name}</p>
                                  <p className="text-xs text-slate-500 dark:text-slate-400">
                                    {agent.executions_count || 0} executions
                                  </p>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className="w-20">
                                  <div className="h-2 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                                    <div 
                                      className={`h-full transition-all ${getProgressColor(agent.score_percent)}`}
                                      style={{ width: `${Math.min(agent.score_percent, 100)}%` }}
                                    />
                                  </div>
                                </div>
                                <span className="text-sm font-medium text-slate-900 dark:text-white">
                                  {agent.score_percent?.toFixed(0)}%
                                </span>
                              </div>
                            </TableCell>
                            <TableCell>
                              {getTrendIcon(agent.trend)}
                            </TableCell>
                            <TableCell>
                              <Badge className={getStatusColor(agent.status)}>
                                {getStatusLabel(agent.status)}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <span className="text-sm text-slate-600 dark:text-slate-300">
                                {agent.threshold_percent}%
                              </span>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                {agent.is_paused && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button variant="ghost" size="icon" className="h-8 w-8 text-green-600" onClick={() => handleResumeAgent(agent)}>
                                        <Play className="h-4 w-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Resume this agent</TooltipContent>
                                  </Tooltip>
                                )}
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openAgentThresholdDialog(agent)}>
                                      <Settings className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Configure threshold</TooltipContent>
                                </Tooltip>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          Page {currentPage} of {totalPages}
                        </p>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Alerts Tab */}
          <TabsContent value="alerts" className="mt-4">
            <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-slate-900 dark:text-white">
                  Recent Alert History ({alertHistory.length})
                </CardTitle>
                <CardDescription className="text-slate-500 dark:text-slate-400">
                  Threshold changes, auto-pauses, and manual interventions
                </CardDescription>
              </CardHeader>
              <CardContent>
                {alertHistory.length === 0 ? (
                  <div className="py-12 text-center">
                    <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
                    <p className="text-lg font-medium text-slate-900 dark:text-white">No Recent Alerts</p>
                    <p className="text-slate-500 dark:text-slate-400">All agents are operating normally</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {alertHistory.map((alert) => (
                      <div key={alert.id} className="flex items-start gap-4 p-3 rounded-lg bg-slate-50 dark:bg-slate-900/50">
                        <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                          alert.type === 'agent_auto_pause' ? 'bg-red-100 dark:bg-red-500/20' :
                          alert.type === 'agent_resume' ? 'bg-green-100 dark:bg-green-500/20' :
                          'bg-blue-100 dark:bg-blue-500/20'
                        }`}>
                          {alert.type === 'agent_auto_pause' && <Pause className="h-4 w-4 text-red-600 dark:text-red-400" />}
                          {alert.type === 'agent_resume' && <Play className="h-4 w-4 text-green-600 dark:text-green-400" />}
                          {alert.type === 'confidence_threshold_update' && <Settings className="h-4 w-4 text-blue-600 dark:text-blue-400" />}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-slate-900 dark:text-white">
                              {alert.type === 'agent_auto_pause' && 'Agent Auto-Paused'}
                              {alert.type === 'agent_resume' && 'Agent Resumed'}
                              {alert.type === 'confidence_threshold_update' && 'Threshold Updated'}
                            </p>
                            {alert.agent_name && (
                              <Badge variant="outline">{alert.agent_name}</Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            By {alert.performed_by} • {alert.timestamp ? new Date(alert.timestamp).toLocaleString() : ''}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Audit Trail Tab */}
          <TabsContent value="audit" className="mt-4">
            <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-slate-900 dark:text-white">
                  Audit Trail
                </CardTitle>
                <CardDescription className="text-slate-500 dark:text-slate-400">
                  All confidence-related actions and changes
                </CardDescription>
              </CardHeader>
              <CardContent>
                {auditTrail.length === 0 ? (
                  <div className="py-12 text-center">
                    <History className="h-12 w-12 mx-auto text-slate-400 mb-4" />
                    <p className="text-lg font-medium text-slate-900 dark:text-white">No Activity</p>
                    <p className="text-slate-500 dark:text-slate-400">No confidence actions recorded yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {auditTrail.map((log) => (
                      <div key={log.id} className="flex items-start gap-4 p-3 rounded-lg bg-slate-50 dark:bg-slate-900/50">
                        <div className="h-8 w-8 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
                          <History className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-slate-900 dark:text-white">{log.action}</p>
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            {log.agent_name && `Agent: ${log.agent_name} • `}
                            By {log.performed_by} • {log.timestamp ? new Date(log.timestamp).toLocaleString() : ''}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Agent Threshold Dialog */}
        <Dialog 
          open={agentThresholdDialog.open} 
          onOpenChange={(open) => setAgentThresholdDialog({ ...agentThresholdDialog, open })}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Configure Agent Threshold
              </DialogTitle>
              <DialogDescription>
                Set a custom confidence threshold for <strong>{agentThresholdDialog.agent?.agent_name}</strong>
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Confidence Threshold</Label>
                  <span className="text-2xl font-bold">{(agentThreshold * 100).toFixed(0)}%</span>
                </div>
                <Slider
                  value={[agentThreshold * 100]}
                  onValueChange={(v) => setAgentThreshold(v[0] / 100)}
                  max={100}
                  min={30}
                  step={5}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>30% (Lenient)</span>
                  <span>60% (Default)</span>
                  <span>100% (Strict)</span>
                </div>
              </div>
              
              <Alert className="border-amber-300 dark:border-amber-500/50 bg-amber-50 dark:bg-amber-950/20">
                <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                <AlertDescription className="text-amber-700 dark:text-amber-300">
                  This will override the global threshold ({(globalThreshold * 100).toFixed(0)}%) for this agent only.
                </AlertDescription>
              </Alert>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setAgentThresholdDialog({ open: false, agent: null })}>
                Cancel
              </Button>
              <Button onClick={handleSaveAgentThreshold}>
                Save Threshold
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Confirmation Dialog */}
        <Dialog open={confirmDialog.open} onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Confirm Threshold Change
              </DialogTitle>
              <DialogDescription>
                You are about to change the global confidence threshold to <strong>{(globalThreshold * 100).toFixed(0)}%</strong>. 
                This will affect all agents that don't have a custom threshold.
              </DialogDescription>
            </DialogHeader>
            
            <Alert className="border-amber-300 dark:border-amber-500/50 bg-amber-50 dark:bg-amber-950/20">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              <AlertDescription className="text-amber-700 dark:text-amber-300">
                Agents with confidence below the new threshold may be automatically paused.
              </AlertDescription>
            </Alert>

            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmDialog({ open: false, type: null, data: null })}>
                Cancel
              </Button>
              <Button onClick={confirmSaveGlobalThreshold}>
                Confirm Change
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
